import { ReactNode } from 'react';
import { useTranslation } from 'react-i18next';

import LessonProgressListSkeleton from '@/components/lesson-progress/LessonProgressListSkeleton';
import EmptyMessage from '@/components/shared/EmptyMessage';
import ErrorMessage from '@/components/shared/ErrorMessage';
import InfinityList, {
  InfinityListOptions,
} from '@/components/ui/custom/InfinityList';

export type UnitLessonCardListProps = Pick<
  InfinityListOptions,
  'onReachEnd' | 'hasNextPage' | 'isFetchingNextPage'
> & {
  isLoading: boolean;
  isEmpty: boolean;
  hasError: boolean;
  onRetry: () => void;
  children: ReactNode;
};

export default function UnitLessonCardList({
  isLoading,
  isEmpty,
  hasError,
  onRetry,
  onReachEnd,
  hasNextPage,
  isFetchingNextPage,
  children,
}: UnitLessonCardListProps) {
  const { t } = useTranslation('translation', {
    keyPrefix: 'courseLessons.lessonList',
  });

  if (isLoading) return <LessonProgressListSkeleton />;

  if (hasError && isEmpty)
    return (
      <ErrorMessage text={t('loadError')} onRetry={onRetry} className="h-24" />
    );

  if (isEmpty) return <EmptyMessage text={t('empty')} className="h-24" />;

  return (
    <InfinityList
      className="h-auto gap-4"
      onReachEnd={onReachEnd}
      hasNextPage={hasNextPage}
      isFetchingNextPage={isFetchingNextPage}
    >
      {children}
    </InfinityList>
  );
}
